import {
    DistributionController, UserController
} from 'controllers';

// functions
import { getCountryCode } from "shared/functions";
import { ShowSuccessToast, ShowErrorToast, ShowWarningToast } from 'shared/functions';

// pipes
import { titleCasePipe } from 'shared/pipes';
import { barcodify } from 'shared/functions/barcodify';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const getData = async (year, month, country) => {
    if (!year || !month) { return null };

    const monthNumber = barcodify(MONTHS.indexOf(month) + 1, 2);
    const countryCode = getCountryCode(country);

    return await DistributionController.getShipmentByDate({ year, monthNumber, countryCode });

}

// Shipment Dates
export const deleteShipment = async (payload, month, country) => {
    let deleted = false;
    if (window.confirm(`Are you sure you would like to delete this shipment:${month} ${payload.partner} ${payload.week} `)) {
        const monthNumber = barcodify(MONTHS.indexOf(month) + 1, 2);
        const year = new Date(payload.shipment_date).getFullYear();
        const countryCode = getCountryCode(country);
        deleted = await DistributionController.deleteShipment({ payload, year, month: monthNumber, countryCode });
    }

    if (deleted) {
        ShowSuccessToast('Successfully deleted!')
    } else {
        ShowErrorToast('Some problem during the deletion.')
    }
}

export const createShipment = async (data, returnDate, shipmentDate, country) => {
    const countryCode = getCountryCode(country);
    const month = barcodify(MONTHS.indexOf(data.month) + 1, 2);
    const year = new Date(shipmentDate).getFullYear();
    const validationFields = { countryCode, month, year, returnDate, shipmentDate };
    let valid = false;

    for (const [key, value] of Object.entries(validationFields)) {
        if (!value) {
            ShowErrorToast(`Value ${titleCasePipe.transform(key)} is required.`)
                ;
            return;
        }

        valid = true;
    };

    const exist = await alreadyExist(data, countryCode, year, month);

    if (exist) {
        ShowWarningToast(`This shipment already exists, please edit it directly.`)
            ;
        return;
    }

    if (valid && !exist) {
        const payload = {
            [data.week]:
            {
                [data.partner]:
                    { return_date: returnDate, shipment_date: shipmentDate }
            }
        };
        return await DistributionController.createShipment({ payload, month, year, countryCode });

    }
}

export const editShipment = async (data, returnDate, shipmentDate, country) => {
    const countryCode = getCountryCode(country);
    const month = barcodify(MONTHS.indexOf(data.month) + 1, 2);
    const year = new Date(shipmentDate).getFullYear();

    const payload = {
        [data.week]:
        {
            [data.partner]:
                { return_date: returnDate, shipment_date: shipmentDate }
        }
    };
    const validationFields = { countryCode, month, year, returnDate, shipmentDate };
    let valid = false;

    for (const [key, value] of Object.entries(validationFields)) {
        if (!value) {
            ShowErrorToast(`Value ${titleCasePipe.transform(key)} is required.`)
                ;
            return valid;
        }

        valid = true;
    };

    if (valid) {
        await DistributionController.editShipment({ payload, month, year, countryCode });
        return payload;
    }
}
export const getPartners = async (country) => {
    const countryCode = getCountryCode(country);
    const results = await DistributionController.getPartnersByMarket(countryCode);

    let list = [];

    for (const r of results) {
        let count = 0;
        do {
            count += 1;
            const label = (r.frequency >= 2) ? `${r.label} ${count}` : r.label;
            list.push(label);
        } while (count < r.frequency);
    }
    return list;
}

// Stylist List
export const createListByWeek = async ({ data, week, month, year, country, pickedPartners }) => {
    const countryCode = getCountryCode(country);
    const monthNumber = barcodify(MONTHS.indexOf(month) + 1, 2);
    let index = 1;

    for (const pickedPartner of pickedPartners) {
        // create the payload for that partner
        const payloadByPartner = await DistributionController.retrievePayloadByWeek({ week, monthNumber, year, countryCode, pickedPartner });

        // retrieve the customers according to week, hyc_status ('active' or 'new customer')
        const shipmentCustomers = await findCustomersByPartner(week, pickedPartner);

        // result: [user_id: {payload}]
        const shipmentList = [];
        for (const shipmentCustomer of shipmentCustomers) {
            const { id, current_stylist } = shipmentCustomer;
            const customerWithPayload = {
                id: id,
                payload: {
                    box_stylist: current_stylist,
                    index: index,
                    ...payloadByPartner
                }
            }
            index += 1;
            shipmentList.push(customerWithPayload);
        }
        // store the ids in the new collection
        const weekCode = week[0] + week[5];
        await DistributionController.setCustomersDocuments({ shipmentList, week: weekCode, month: monthNumber, year, countryCode });
    }
}

// retrieve the customers according to week, partner, hyc_status ('active' or 'new customer')
const findCustomersByPartner = async (week, pickedPartner) => {

    const queriesActive = [{ field: 'hyc_status', search: 'active' }, { field: 'delivery_week', search: week }, { field: 'distribution_partner', search: pickedPartner }];
    const activeCustomersByPartner = await UserController.getUsersByThreeFields(queriesActive);

    const queriesNewCustomers = [{ field: 'hyc_status', search: 'new_customers' }, { field: 'delivery_week', search: week }, { field: 'distribution_partner', search: pickedPartner }];
    const newCustomersByPartner = await UserController.getUsersByThreeFields(queriesNewCustomers);

    return [...activeCustomersByPartner, ...newCustomersByPartner];
}


export const validation = async ({ data, week, month, year, country, pickedPartners }) => {
    const noDatesError = new Error('No shipment dates for that week. Please create one before generating the list.')

    if (!data) throw noDatesError;

    const datesExist = checkByWeekPartner(data, week, pickedPartners);
    // if false, they should create shipment dates
    if (!datesExist) throw noDatesError;

    const alreadyDone = await alreadyGenerated(data, week, country, year, month);

    // if fails: already generated
    if (alreadyDone) throw new Error('Already done earlier! The list for that week has already been generated.');
}

const checkByWeekPartner = (data, week, pickedPartners) => {
    const weekExist = data.hasOwnProperty(week);

    if (weekExist) {
        const result = [];
        for (const partner of pickedPartners) {
            result.push(data[week].hasOwnProperty(partner));

        }
        return result.every(x => x === true);
    }

    return false;
}

/**
 * check if the list has been already created by retrieving the subcollection `${countryCode}_${year}_${monthNumber}_${weekCode}`
 * @param {array} input
 * @param {string} week
 * @param {string} countryCode
 * @param {number} year
 * @param {string} month
 * @returns Boolean
 */
export const alreadyGenerated = async (input, week, country, year, month) => {
    const weekExist = input.hasOwnProperty(week);

    if (weekExist) {
        const monthNumber = barcodify(MONTHS.indexOf(month) + 1, 2);
        const countryCode = getCountryCode(country);
        const weekCode = week[0] + week[5];
        const result = await DistributionController.getShipmentListsByDate({ year, monthNumber, weekCode, countryCode });
        return result.length !== 0;
    }

    return false;
}

const alreadyExist = async (data, countryCode, year, month) => {
    const result = await DistributionController.getShipmentByDate({ year, monthNumber: month, countryCode });
    const weekExist = result?.hasOwnProperty(data.week);
    let alreadyExist = false;

    if (weekExist) {
        alreadyExist = result[data.week].hasOwnProperty(data.partner);
    }

    return alreadyExist;
}

